import React, { useEffect, useState } from 'react';
import {
   Box,
   Grid,
   Card,
   CardMedia,
   CardContent,
   CardActions,
   Typography,
   IconButton,
   Button,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Tooltip,
   Chip,
   Menu,
   MenuItem,
   ListItemIcon,
   ListItemText,
   Divider,
   Slider,
   Snackbar,
   Alert,
   AlertColor,
} from '@mui/material';
import {
   Share as ShareIcon,
   Download as DownloadIcon,
   Delete as DeleteIcon,
   Facebook as FacebookIcon,
   Twitter as TwitterIcon,
   LinkedIn as LinkedInIcon,
   Instagram as InstagramIcon,
   Email as EmailIcon,
   WhatsApp as WhatsAppIcon,
   ContentCopy as CopyLinkIcon,
   MoreVert as MoreIcon,
   CompareArrows as CompareIcon,
   Fullscreen as FullscreenIcon,
   ArrowForwardIos,
   ArrowBackIos,
   Save as SaveIcon,
} from '@mui/icons-material';
import { TransformedPhoto } from '../types';
import { get_user_transformed_photos } from '../../../../services';
import { useUser } from '../../../../contexts/UserContext';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../../firebase';

interface TransformedGalleryProps {
   onDelete?: (photoId: string) => void;

   savePhoto: (photo: TransformedPhoto) => Promise<void>;
}

interface Photo {
   id: string;
   url: string;
   createdAt: Date;
   type: string;
   tripId?: string;
   eventId?: string;
   userId?: string;
}

const MyTransformedGallery: React.FC<TransformedGalleryProps> = ({ savePhoto, onDelete }) => {
   const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
   const [detailDialogOpen, setDetailDialogOpen] = useState(false);

   const [shareMenuAnchorEl, setShareMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [activePhotoId, setActivePhotoId] = useState<string | null>(null);
   const [photos, setPhotos] = useState<Photo[]>([]);
   const { currentUser } = useUser();

   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState('info');
   const [snackbarOpen, setSnackbarOpen] = useState(false);

   // Handle opening the share menu
   const handleOpenShareMenu = (event: React.MouseEvent<HTMLButtonElement>, photoId: string) => {
      setShareMenuAnchorEl(event.currentTarget);
      setActivePhotoId(photoId);
   };

   // Handle closing the share menu
   const handleCloseShareMenu = () => {
      setShareMenuAnchorEl(null);
      setActivePhotoId(null);
   };

   // Handle opening the options menu
   const handleOpenOptionsMenu = (event: React.MouseEvent<HTMLButtonElement>, photoId: string) => {
      setOptionsMenuAnchorEl(event.currentTarget);
      setActivePhotoId(photoId);
   };

   // Handle closing the options menu
   const handleCloseOptionsMenu = () => {
      setOptionsMenuAnchorEl(null);
      setActivePhotoId(null);
   };

   // Handle sharing on a platform
   const handleShare = (url: string) => {
      navigator.clipboard.writeText(url);
      setSnackbarMessage('Link copied to clipboard');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      handleCloseShareMenu();
   };

   // Handle downloading a photo
   const handleDownload = async (url: string) => {
      try {
         const response = await fetch(url);
         const blob = await response.blob();
         const blobUrl = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = blobUrl;
         // Get filename from URL or use a default name
         const filename = url.split('/').pop() || 'transformed-image.jpg';
         link.download = filename;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(blobUrl);

         setSnackbarMessage('Download started successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         setSnackbarMessage('Failed to download image');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   const handleDelete = async (photoId: string) => {
      try {
         await deleteDoc(doc(db, 'photos', photoId));
         setSnackbarMessage('Photo deleted successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         setSnackbarMessage('Failed to delete photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   // Format date for display
   const formatDate = (date: Date) => {
      return new Date(date).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
      });
   };

   // Handle saving a photo
   const handleSave = async (photoId: string) => {
      try {
         const photo = photos.find((p) => p.id === photoId);

         // if (photo) {
         //    await savePhoto(photo);
         // }
      } catch (error) {}
   };

   const loadPhotos = async () => {
      const photos = await get_user_transformed_photos(currentUser?.uid || '');

      setPhotos(
         photos.map((photo: any) => ({
            ...photo,
            transformedUrl: photo.url,
            thumbnailUrl: photo.url,
            stylePreset: photo.stylePreset,
            createdAt: photo.createdAt.toDate(),
            type: photo.type,
            tripId: photo.tripId,
            eventId: photo.eventId,
            userId: photo.userId,
            originalPhotoId: photo.id,
         }))
      );
   };

   useEffect(() => {
      loadPhotos();
   }, []);

   return (
      <Box>
         {photos.length === 0 ? (
            <Typography variant='body2' color='text.secondary' align='center' sx={{ p: 3 }}>
               No transformed photos yet. Transform some photos to see them here.
            </Typography>
         ) : (
            <>
               <Grid container spacing={3}>
                  {photos.map((photo) => (
                     <Grid
                        onClick={() => {
                           setSelectedPhoto(photo);
                           setDetailDialogOpen(true);
                        }}
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        lg={3}
                        key={photo.id}
                        sx={{ cursor: 'pointer' }}
                     >
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                           <CardMedia
                              component='img'
                              height={220}
                              image={photo.url}
                              alt={`Transformed photo ${photo.id}`}
                              sx={{ cursor: 'pointer', objectFit: 'cover' }}
                           />
                           <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                              {photo?.createdAt && (
                                 <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    display='block'
                                 >
                                    Created {`${formatDate(photo?.createdAt)}`}
                                 </Typography>
                              )}
                           </CardContent>
                           {/* display on hover */}
                           <CardActions
                              sx={{
                                 justifyContent: 'space-between',
                                 pt: 0,

                                 width: '100%',
                              }}
                           >
                              <Box>
                                 <Tooltip title='Share'>
                                    <IconButton
                                       sx={{ color: 'primary.main' }}
                                       size='small'
                                       onClick={(e) => handleOpenShareMenu(e, photo.id)}
                                       aria-label='share'
                                    >
                                       <ShareIcon fontSize='small' />
                                    </IconButton>
                                 </Tooltip>
                                 <Tooltip title='Download'>
                                    <IconButton
                                       sx={{ color: 'primary.main' }}
                                       size='small'
                                       onClick={() => handleDownload(photo.url || '')}
                                       aria-label='download'
                                    >
                                       <DownloadIcon fontSize='small' />
                                    </IconButton>
                                 </Tooltip>
                                 <Tooltip title='Delete'>
                                    <IconButton
                                       sx={{ color: 'red' }}
                                       size='small'
                                       onClick={() => handleDelete(photo.id)}
                                       aria-label='delete'
                                    >
                                       <DeleteIcon fontSize='small' />
                                    </IconButton>
                                 </Tooltip>
                              </Box>
                           </CardActions>
                        </Card>
                     </Grid>
                  ))}
               </Grid>

               <Menu
                  anchorEl={shareMenuAnchorEl}
                  open={Boolean(shareMenuAnchorEl)}
                  onClose={handleCloseShareMenu}
               >
                  {/* <MenuItem onClick={() => handleShare('facebook')}>
                     <ListItemIcon>
                        <FacebookIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Facebook</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleShare('twitter')}>
                     <ListItemIcon>
                        <TwitterIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Twitter</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleShare('linkedin')}>
                     <ListItemIcon>
                        <LinkedInIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>LinkedIn</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleShare('instagram')}>
                     <ListItemIcon>
                        <InstagramIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Instagram</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleShare('email')}>
                     <ListItemIcon>
                        <EmailIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Email</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleShare('whatsapp')}>
                     <ListItemIcon>
                        <WhatsAppIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>WhatsApp</ListItemText>
                  </MenuItem>
                  <Divider /> */}
                  <MenuItem onClick={() => handleShare(selectedPhoto?.url || '')}>
                     <ListItemIcon>
                        <CopyLinkIcon fontSize='small' />
                     </ListItemIcon>
                     <ListItemText>Copy Link</ListItemText>
                  </MenuItem>
               </Menu>

               <Dialog
                  open={detailDialogOpen}
                  onClose={() => setDetailDialogOpen(false)}
                  maxWidth='md'
                  fullWidth
               >
                  {selectedPhoto && (
                     <>
                        <DialogTitle>
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                              }}
                           >
                              {selectedPhoto.createdAt && (
                                 <Chip
                                    label={`${formatDate(selectedPhoto.createdAt)}`}
                                    size='small'
                                    variant='outlined'
                                 />
                              )}
                           </Box>
                        </DialogTitle>
                        <DialogContent dividers>
                           <Box
                              sx={{
                                 background: `url(${selectedPhoto?.url}) no-repeat center center `,
                                 display: 'flex',
                                 justifyContent: 'center',
                                 mb: 2,
                                 width: '100%',
                                 height: '400px',
                              }}
                           ></Box>
                        </DialogContent>
                        <DialogActions>
                           <Button
                              startIcon={<SaveIcon />}
                              onClick={() => handleSave(selectedPhoto.id)}
                           >
                              Save
                           </Button>

                           <Button
                              startIcon={<ShareIcon />}
                              onClick={(e) => handleOpenShareMenu(e, selectedPhoto.id)}
                           >
                              Share
                           </Button>
                           <Button
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownload(selectedPhoto?.url || '')}
                           >
                              Download
                           </Button>
                           <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
                        </DialogActions>
                     </>
                  )}
               </Dialog>
            </>
         )}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity as AlertColor}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default MyTransformedGallery;
